import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useCart, useApplyCoupon, useRemoveCoupon } from '../hooks/useCart';
import { useCurrency } from '../hooks/useCurrency';
import { useShippingZones, useSettings, useBranches } from '../hooks/usePublicContent';
import type { Order, ShippingArea, CartBundle } from '../types';
import { Container } from '../components/ui/Container';
import { EditorialImage } from '../components/ui/EditorialImage';

// ─── Step Indicator ───────────────────────────────────────────────────────────

const STEPS = ['Contact', 'Shipping', 'Review'];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0 mb-10">
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={label} className="flex items-center">
            <div className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold transition-colors ${
                  done
                    ? 'border-moss bg-moss text-cream'
                    : active
                    ? 'border-ink bg-ink text-cream'
                    : 'border-stone-light bg-cream text-stone'
                }`}
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span className={`text-xs font-medium ${active ? 'text-ink' : done ? 'text-moss' : 'text-stone'}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`mx-3 h-px w-8 sm:w-16 ${i < current ? 'bg-moss' : 'bg-stone-light'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Floating Label Input ─────────────────────────────────────────────────────

function FloatingInput({
  label, value, onChange, type = 'text', required = true, autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  const [focused, setFocused] = useState(false);
  const lifted = focused || value.length > 0;

  return (
    <div className="relative">
      <input
        type={type}
        required={required}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`peer w-full rounded-xl border bg-paper px-4 pb-2.5 pt-5 text-sm text-ink outline-none transition-colors ${
          focused ? 'border-ink shadow-sm' : 'border-stone-light hover:border-stone'
        }`}
      />
      <label
        className={`pointer-events-none absolute left-4 text-stone transition-all duration-200 ${
          lifted ? 'top-1.5 text-[10px] font-semibold uppercase tracking-wider' : 'top-1/2 -translate-y-1/2 text-sm'
        } ${focused ? 'text-ink' : ''}`}
      >
        {label}{required && <span className="ml-0.5 text-clay">*</span>}
      </label>
    </div>
  );
}

function FloatingSelect({
  label, value, onChange, children, required = true,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
  required?: boolean;
}) {
  const lifted = value.length > 0;

  return (
    <div className="relative">
      <select
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`peer w-full appearance-none rounded-xl border bg-paper px-4 pb-2.5 pt-5 text-sm text-ink outline-none transition-colors focus:border-ink focus:shadow-sm ${
          lifted ? 'border-stone-light hover:border-stone' : 'border-stone-light hover:border-stone'
        }`}
      >
        {children}
      </select>
      <label
        className={`pointer-events-none absolute left-4 text-stone transition-all duration-200 ${
          lifted ? 'top-1.5 text-[10px] font-semibold uppercase tracking-wider' : 'top-1/2 -translate-y-1/2 text-sm'
        }`}
      >
        {label}{required && <span className="ml-0.5 text-clay">*</span>}
      </label>
      <svg
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-stone"
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      >
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  );
}

// ─── Coupon Section ───────────────────────────────────────────────────────────

function CouponBox({ customerEmail }: { customerEmail?: string }) {
  const { data: cart } = useCart();
  const { format } = useCurrency();
  const applyCoupon = useApplyCoupon();
  const removeCoupon = useRemoveCoupon();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // A code is "applied" when either the legacy coupon or a promotion coupon is on the cart
  const appliedCode = cart?.promotion_coupon_code ?? cart?.coupon_code ?? null;
  const isPromoCode = !!cart?.promotion_coupon_code && !cart?.coupon_code;

  // Saving amount to display next to the applied code label
  const appliedSaving = isPromoCode
    ? (cart?.promotion_discount_total ?? 0)
    : (cart?.coupon_discount ?? 0);

  const handleApply = () => {
    if (!code.trim()) return;
    setError(null);
    applyCoupon.mutate({ code: code.trim(), customer_email: customerEmail }, {
      onSuccess: () => { setCode(''); setOpen(false); },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg ?? 'Invalid coupon code.');
      },
    });
  };

  // When a code is applied: show applied state — input is completely hidden/disabled
  if (appliedCode) {
    return (
      <div className="flex items-center justify-between rounded-xl border border-moss/30 bg-moss/5 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-moss">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <span>{appliedCode}</span>
          {appliedSaving > 0 && (
            <span className="text-moss/70 font-normal">— saving {format(appliedSaving)}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => removeCoupon.mutate()}
          disabled={removeCoupon.isPending}
          className="text-xs text-stone underline hover:text-clay disabled:opacity-40"
        >
          {removeCoupon.isPending ? '…' : 'Remove'}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-stone-light overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm text-ink-soft"
      >
        <span className="flex items-center gap-2 font-medium">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-stone">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          Have a coupon code?
        </span>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={`text-stone transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className="border-t border-stone-light bg-paper px-4 pb-4 pt-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              placeholder="Enter code"
              autoCapitalize="characters"
              className="flex-1 rounded-lg border border-stone-light bg-cream px-3 py-2.5 font-mono text-sm uppercase tracking-widest text-ink placeholder:text-stone/60 placeholder:normal-case placeholder:tracking-normal focus:border-ink focus:outline-none"
            />
            <button
              type="button"
              onClick={handleApply}
              disabled={applyCoupon.isPending || !code.trim()}
              className="shrink-0 rounded-lg bg-ink px-4 text-xs font-semibold uppercase tracking-widest text-cream hover:bg-clay disabled:opacity-40"
            >
              {applyCoupon.isPending ? '…' : 'Apply'}
            </button>
          </div>
          {error && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-clay">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Checkout Bundle Row ──────────────────────────────────────────────────────

function CheckoutBundleRow({ bundle, format }: { bundle: CartBundle; format: (v: number) => string }) {
  const API_URL = import.meta.env.VITE_API_URL as string;
  const imgSrc = bundle.bundle_image
    ? (bundle.bundle_image.startsWith('http') ? bundle.bundle_image : `${API_URL}${bundle.bundle_image}`)
    : null;
  const lineTotal = Number(bundle.unit_price) * bundle.quantity;

  return (
    <div className="py-3">
      <div className="flex items-center gap-3">
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg">
          {imgSrc ? (
            <img src={imgSrc} alt={bundle.bundle_name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-parchment text-lg">📦</div>
          )}
          <span className="absolute -right-1 -top-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-ink text-[9px] font-bold text-cream leading-none p-0.5">
            {bundle.quantity}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink line-clamp-1 leading-snug">{bundle.bundle_name}</p>
          <p className="text-xs text-stone">{bundle.products.length} {bundle.products.length === 1 ? 'product' : 'products'} included</p>
        </div>
        <p className="text-sm font-medium text-ink tabular-nums shrink-0">{format(lineTotal)}</p>
      </div>
      {/* Included products preview */}
      <div className="mt-2 ml-15 space-y-1">
        {bundle.products.map((p, i) => (
          <p key={i} className="truncate text-xs text-stone/80">
            ✓ {p.product_name} — {[p.size_label, p.frame_name, p.material_name].filter(Boolean).join(' · ') || 'Standard'}
          </p>
        ))}
      </div>
    </div>
  );
}

// ─── Order Summary ────────────────────────────────────────────────────────────

function OrderSummary({
  shippingFee, estimatedDays, customerEmail,
}: {
  shippingFee: number | null;
  estimatedDays: string | null;
  customerEmail?: string;
}) {
  const { data: cart } = useCart(customerEmail || undefined);
  const { format } = useCurrency();

  const items = cart?.items ?? [];
  const bundles = cart?.bundles ?? [];
  const subtotal = cart?.subtotal ?? items.reduce((sum, item) => sum + Number(item.unit_price) * item.quantity, 0) + bundles.reduce((sum, b) => sum + Number(b.unit_price) * b.quantity, 0);
  const bundleDiscount = bundles.reduce((sum, b) => sum + Number(b.discount_amount) * b.quantity, 0);
  const displaySubtotal = subtotal + bundleDiscount;
  const saleDiscount = cart?.sale_discount ?? 0;
  const freeItems = cart?.free_items ?? [];
  const couponDiscount = cart?.coupon_discount ?? 0;
  const couponCode = cart?.coupon_code ?? null;
  const promotionDiscounts = cart?.promotion_discounts ?? [];
  const promotionDiscountTotal = cart?.promotion_discount_total ?? 0;
  const baseTotal = cart?.total ?? subtotal;
  const grandTotal = baseTotal + (shippingFee ?? 0);
  const totalSavings = saleDiscount + couponDiscount + promotionDiscountTotal + bundleDiscount;

  return (
    <div className="space-y-4">
      {/* Items */}
      <div className="rounded-2xl border border-stone-light bg-paper overflow-hidden">
        <div className="border-b border-stone-light px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone">Order Summary</p>
        </div>
        <div className="divide-y divide-stone-light/60 px-5">
          {items.map((item) => {
            const _p = item.product?.images?.[0]?.path;
            const imgSrc = _p ? (_p.startsWith('http') ? _p : `${import.meta.env.VITE_API_URL}${_p}`) : null;
            const name = item.product?.name ?? `Product #${item.product_id}`;
            return (
              <div key={item.id} className="flex items-center gap-3 py-3">
                <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg">
                  {imgSrc
                    ? <img src={imgSrc} alt={name} className="h-full w-full object-cover" />
                    : <EditorialImage seed={`co-${item.id}`} alt={name} duotone={false} size="thumb" aspect="aspect-square" className="h-full w-full" />
                  }
                  <span className="absolute -right-1 -top-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-ink text-[9px] font-bold text-cream leading-none p-0.5">
                    {item.quantity}
                  </span>
                </div>
                <p className="flex-1 min-w-0 text-sm text-ink-soft line-clamp-2 leading-snug">{name}</p>
                <p className="text-sm font-medium text-ink tabular-nums shrink-0">
                  {format(Number(item.unit_price) * item.quantity)}
                </p>
              </div>
            );
          })}
          {bundles.map((bundle) => (
            <CheckoutBundleRow key={`bundle-${bundle.id}`} bundle={bundle} format={format} />
          ))}
        </div>
      </div>

      {/* Coupon */}
      <CouponBox customerEmail={customerEmail} />

      {/* Totals */}
      <div className="rounded-2xl border border-stone-light bg-paper px-5 py-4 space-y-2.5">
        <div className="flex justify-between text-sm text-ink-soft">
          <span>Subtotal</span><span className="tabular-nums">{format(displaySubtotal)}</span>
        </div>
        {bundleDiscount > 0 && (
          <div className="flex justify-between text-sm text-moss">
            <span>Bundle discount</span>
            <span className="tabular-nums">−{format(bundleDiscount)}</span>
          </div>
        )}
        {freeItems.length > 0
          ? freeItems.map((free, idx) => (
              <div key={idx} className="flex justify-between text-sm text-moss">
                <span>{free.sale_name}: {free.quantity} free</span>
                <span className="tabular-nums">−{format(free.free_unit_price * free.quantity)}</span>
              </div>
            ))
          : saleDiscount > 0 && (
              <div className="flex justify-between text-sm text-moss">
                <span>Sale discount</span>
                <span className="tabular-nums">−{format(saleDiscount)}</span>
              </div>
            )
        }
        {couponDiscount > 0 && couponCode && (
          <div className="flex justify-between text-sm text-moss">
            <span>Coupon ({couponCode})</span>
            <span className="tabular-nums">−{format(couponDiscount)}</span>
          </div>
        )}
        {promotionDiscounts.map((pd) => (
          <div key={pd.promotion_id} className="flex justify-between text-sm text-moss">
            <span>{pd.description}</span>
            <span className="tabular-nums">−{format(pd.discount)}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm text-ink-soft">
          <span>Shipping</span>
          <span className="tabular-nums">
            {shippingFee === null
              ? <span className="text-stone">Select area</span>
              : shippingFee === 0
              ? <span className="text-moss font-medium">Free</span>
              : format(shippingFee)
            }
          </span>
        </div>
        {estimatedDays && (
          <p className="text-right text-xs text-stone">Est. {estimatedDays}</p>
        )}
        <div className="flex justify-between border-t border-stone-light pt-3 text-base font-semibold text-ink">
          <span>Total</span>
          <span className="tabular-nums">{format(grandTotal)}</span>
        </div>
        {totalSavings > 0 && (
          <div className="rounded-lg bg-moss/8 border border-moss/15 px-3 py-2 text-xs text-moss font-medium flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="shrink-0"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>
            Saving {format(totalSavings)} on this order
          </div>
        )}
      </div>

      {/* Security strip */}
      <div className="flex items-center justify-center gap-5 py-1">
        {[
          { icon: '🔒', label: 'SSL Secured' },
          { icon: '✓', label: 'Safe Payment' },
          { icon: '↩', label: 'Easy Returns' },
        ].map((b) => (
          <div key={b.label} className="flex items-center gap-1 text-[11px] text-stone">
            <span>{b.icon}</span>
            {b.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Checkout Page ────────────────────────────────────────────────────────────

export function CheckoutPage() {
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    address_line2: '',
    notes: '',
  });

  const { data: cart } = useCart(form.customer_email || undefined);
  const { format } = useCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: shippingZones = [] } = useShippingZones();
  const { data: settings } = useSettings();
  const { data: branches = [] } = useBranches();

  const [step, setStep] = useState(0);
  const [fulfillmentType, setFulfillmentType] = useState<'delivery' | 'pickup'>('delivery');
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const isPickup = fulfillmentType === 'pickup';

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const allCities = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const zone of shippingZones) {
      for (const city of zone.cities) {
        if (!seen.has(city.name)) {
          seen.add(city.name);
          out.push(city.name);
        }
      }
    }
    return out.sort();
  }, [shippingZones]);

  const areasForCity = useMemo((): ShippingArea[] => {
    if (!selectedCity) return [];
    for (const zone of shippingZones) {
      const city = zone.cities.find((c) => c.name === selectedCity);
      if (city) return city.areas;
    }
    return [];
  }, [shippingZones, selectedCity]);

  const selectedArea = useMemo(
    () => areasForCity.find((a) => a.id === selectedAreaId) ?? null,
    [areasForCity, selectedAreaId],
  );

  const hasShippingZones = allCities.length > 0;
  const items = cart?.items ?? [];
  const bundles = cart?.bundles ?? [];
  const hasCartItems = items.length > 0 || bundles.length > 0;
  const baseTotal = cart?.total ?? 0;

  // Global shipping settings from admin
  const globalShippingPrice = Number(settings?.shipping?.price ?? 0);
  const freeThreshold = settings?.shipping?.free_threshold != null
    ? Number(settings.shipping.free_threshold)
    : null;

  // Compute the effective fee for an area, mirroring the backend:
  //   area.delivery_fee > 0  → use area fee
  //   area.delivery_fee === 0 → fall back to global price (with free threshold)
  const effectiveFeeForArea = (fee: number): number => {
    if (fee > 0) return fee;
    if (freeThreshold !== null && baseTotal >= freeThreshold) return 0;
    return globalShippingPrice;
  };

  const effectiveDeliveryTime = (area: ShippingArea): string | null => {
    if (area.delivery_fee > 0) return area.estimated_days ?? null;
    return settings?.shipping?.default_delivery_time ?? area.estimated_days ?? null;
  };

  // Overall shipping fee for the order:
  //   - Pickup: always free
  //   - Promotion free-shipping wins
  //   - Zone area selected → effective area fee
  //   - No zones at all → global price with threshold
  //   - Zones exist but nothing selected yet → null ("Select area")
  let shippingFee: number | null;
  if (isPickup) {
    shippingFee = 0;
  } else if (cart?.free_shipping) {
    shippingFee = 0;
  } else if (selectedArea) {
    shippingFee = effectiveFeeForArea(selectedArea.delivery_fee);
  } else if (!hasShippingZones) {
    shippingFee = freeThreshold !== null && baseTotal >= freeThreshold ? 0 : globalShippingPrice;
  } else {
    shippingFee = null;
  }

  const grandTotal = baseTotal + (shippingFee ?? 0);

  const placeOrder = useMutation({
    mutationFn: async () =>
      (
        await api.post<Order>('/checkout', {
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone || undefined,
          fulfillment_type: fulfillmentType,
          ...(isPickup
            ? { branch_id: selectedBranchId }
            : {
                shipping_address: {
                  address_line1: form.address_line1,
                  address_line2: form.address_line2 || undefined,
                  city: selectedCity || undefined,
                  area: selectedArea?.name || undefined,
                },
                ...(selectedAreaId ? { shipping_area_id: selectedAreaId } : {}),
              }),
          notes: form.notes || undefined,
        })
      ).data,
    onSuccess: (order) => {
      queryClient.setQueriesData({ queryKey: ['cart'] }, null);
      navigate(`/orders/${order.order_number}`);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setCheckoutError(msg ?? 'Something went wrong. Please try again.');
    },
  });

  const f = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (v: string) => setForm((p) => ({ ...p, [key]: v })),
  });

  const canProceedStep0 =
    form.customer_name.trim() &&
    form.customer_email.trim() &&
    form.customer_phone.trim() &&
    (isPickup ? selectedBranchId !== null : form.address_line1.trim());

  const canProceedStep1 =
    isPickup || !hasShippingZones || (selectedCity && selectedAreaId !== null);

  return (
    <Container className="py-12 md:py-16">
      <StepIndicator current={step} />

      <div className="grid gap-10 lg:grid-cols-[1fr_400px] lg:items-start">
        {/* Left column — form */}
        <div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 0 && isPickup) {
                setStep(2); // skip delivery area step for pickup
              } else if (step < 2) {
                setStep((s) => s + 1);
              } else {
                setCheckoutError(null);
                placeOrder.mutate();
              }
            }}
          >
            {/* Step 0 — Contact & Address */}
            {step === 0 && (
              <div className="space-y-5">
                <div>
                  <h2 className="font-serif text-xl font-light text-ink mb-5">Contact information</h2>
                  <div className="space-y-4">
                    <FloatingInput label="Full name" autoComplete="name" {...f('customer_name')} />
                    <FloatingInput label="Email address" type="email" autoComplete="email" {...f('customer_email')} />
                    <FloatingInput label="Phone number" type="tel" autoComplete="tel" {...f('customer_phone')} />
                  </div>
                </div>

                <div className="border-t border-stone-light pt-6">
                  <h2 className="font-serif text-xl font-light text-ink mb-4">Fulfillment</h2>

                  {/* Delivery / Pickup toggle */}
                  <div className="mb-5 flex gap-2">
                    {([
                      { value: 'delivery', label: 'Home Delivery', icon: '🚚' },
                      { value: 'pickup',   label: 'Pick up from Branch', icon: '🏪' },
                    ] as const).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setFulfillmentType(opt.value); setSelectedBranchId(null); }}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-xl border py-3 text-sm font-medium transition-all ${
                          fulfillmentType === opt.value
                            ? 'border-ink bg-ink text-cream'
                            : 'border-stone-light bg-paper text-ink-soft hover:border-stone'
                        }`}
                      >
                        <span>{opt.icon}</span>
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {isPickup ? (
                    <div className="space-y-3">
                      {branches.length === 0 ? (
                        <p className="text-sm text-stone">No branches available for pickup.</p>
                      ) : branches.map((branch: any) => (
                        <button
                          key={branch.id}
                          type="button"
                          onClick={() => setSelectedBranchId(branch.id)}
                          className={`w-full rounded-xl border p-4 text-left transition-all ${
                            selectedBranchId === branch.id
                              ? 'border-ink bg-ink/5 ring-1 ring-ink'
                              : 'border-stone-light hover:border-stone'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-ink">{branch.name}</p>
                              {branch.city && <p className="mt-0.5 text-sm text-stone">{branch.city}</p>}
                              {branch.address && <p className="mt-0.5 text-xs text-stone">{branch.address}</p>}
                              {branch.working_hours && (
                                <p className="mt-1 text-xs text-stone/70">Hours: {branch.working_hours}</p>
                              )}
                            </div>
                            <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition-colors ${
                              selectedBranchId === branch.id ? 'border-ink bg-ink' : 'border-stone-light'
                            }`} />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <FloatingInput label="Address line 1" autoComplete="address-line1" {...f('address_line1')} />
                      <FloatingInput label="Address line 2" required={false} autoComplete="address-line2" {...f('address_line2')} />
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canProceedStep0}
                  className="w-full rounded-full bg-ink py-3.5 text-[13px] font-semibold uppercase tracking-widest text-cream transition-all hover:bg-clay disabled:opacity-40 active:scale-[0.98]"
                >
                  {isPickup ? 'Review order' : 'Continue to shipping'}
                </button>
              </div>
            )}

            {/* Step 1 — Delivery area */}
            {step === 1 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-2">
                  <button type="button" onClick={() => setStep(0)} className="text-stone hover:text-ink transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>
                  <h2 className="font-serif text-xl font-light text-ink">Delivery details</h2>
                </div>

                {/* Contact summary */}
                <div className="rounded-xl border border-stone-light bg-paper px-4 py-3 flex justify-between items-center">
                  <div className="text-sm text-ink-soft">
                    <p className="font-medium text-ink">{form.customer_name}</p>
                    <p>{form.customer_email}</p>
                  </div>
                  <button type="button" onClick={() => setStep(0)} className="text-xs text-stone underline hover:text-ink">Edit</button>
                </div>

                {/* Global shipping notice when no zones configured */}
                {!hasShippingZones && (
                  <div className="rounded-xl border border-stone-light bg-paper px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-stone mb-1">Shipping</p>
                      <p className="text-sm text-ink">
                        {shippingFee === 0
                          ? 'Free shipping on this order'
                          : `${format(globalShippingPrice)} flat rate`}
                      </p>
                      {freeThreshold !== null && shippingFee !== 0 && (
                        <p className="text-xs text-stone mt-0.5">
                          Free shipping on orders over {format(freeThreshold)}
                        </p>
                      )}
                    </div>
                    {shippingFee === 0 && (
                      <span className="text-xs font-semibold text-moss">Free</span>
                    )}
                  </div>
                )}

                {/* City + area selection */}
                {hasShippingZones ? (
                  <div className="space-y-4">
                    <FloatingSelect
                      label="City / Governorate"
                      value={selectedCity}
                      onChange={(v) => { setSelectedCity(v); setSelectedAreaId(null); }}
                    >
                      <option value="" />
                      {allCities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </FloatingSelect>

                    {selectedCity && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-stone">Select area *</p>
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                          {areasForCity.map((area) => {
                            const displayFee = effectiveFeeForArea(area.delivery_fee);
                            return (
                              <button
                                key={area.id}
                                type="button"
                                onClick={() => setSelectedAreaId(area.id)}
                                className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
                                  selectedAreaId === area.id
                                    ? 'border-ink bg-ink text-cream'
                                    : 'border-stone-light bg-paper text-ink-soft hover:border-ink/30'
                                }`}
                              >
                                <span className="font-medium">{area.name}</span>
                                <div className="text-right shrink-0">
                                  <p className={`text-sm font-semibold ${selectedAreaId === area.id ? 'text-cream' : 'text-ink'}`}>
                                    {displayFee === 0 ? 'Free' : format(displayFee)}
                                  </p>
                                  {effectiveDeliveryTime(area) && (
                                    <p className={`text-xs ${selectedAreaId === area.id ? 'text-cream/70' : 'text-stone'}`}>
                                      {effectiveDeliveryTime(area)}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <FloatingInput
                    label="City"
                    value={selectedCity}
                    onChange={setSelectedCity}
                  />
                )}

                {/* Order notes */}
                <div className="relative">
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                    rows={3}
                    placeholder="Order notes (optional) — special instructions, gift message…"
                    className="w-full resize-none rounded-xl border border-stone-light bg-paper px-4 py-3 text-sm text-ink placeholder:text-stone/60 focus:border-ink focus:outline-none focus:shadow-sm hover:border-stone transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!canProceedStep1}
                  className="w-full rounded-full bg-ink py-3.5 text-[13px] font-semibold uppercase tracking-widest text-cream transition-all hover:bg-clay disabled:opacity-40 active:scale-[0.98]"
                >
                  Continue to review
                </button>
              </div>
            )}

            {/* Step 2 — Review & Place Order */}
            {step === 2 && (
              <div className="space-y-5">
                <div className="flex items-center gap-3 mb-2">
                  <button type="button" onClick={() => setStep(isPickup ? 0 : 1)} className="text-stone hover:text-ink transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>
                  <h2 className="font-serif text-xl font-light text-ink">Review your order</h2>
                </div>

                {/* Contact summary */}
                <div className="rounded-xl border border-stone-light bg-paper divide-y divide-stone-light/60">
                  <div className="px-4 py-3 flex justify-between items-center">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-stone mb-1">Contact</p>
                      <p className="text-sm text-ink">{form.customer_name} · {form.customer_email}</p>
                      {form.customer_phone && <p className="text-sm text-ink-soft">{form.customer_phone}</p>}
                    </div>
                    <button type="button" onClick={() => setStep(0)} className="text-xs text-stone underline hover:text-ink shrink-0">Edit</button>
                  </div>

                  {isPickup ? (
                    (() => {
                      const branch = branches.find((b: any) => b.id === selectedBranchId);
                      return (
                        <div className="px-4 py-3 flex justify-between items-center">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-stone mb-1">Pick up at</p>
                            {branch ? (
                              <>
                                <p className="text-sm text-ink">{branch.name}</p>
                                {branch.address && <p className="text-sm text-ink-soft">{branch.address}{branch.city ? `, ${branch.city}` : ''}</p>}
                              </>
                            ) : (
                              <p className="text-sm text-stone">No branch selected</p>
                            )}
                          </div>
                          <button type="button" onClick={() => setStep(0)} className="text-xs text-stone underline hover:text-ink shrink-0">Edit</button>
                        </div>
                      );
                    })()
                  ) : (
                    <>
                      <div className="px-4 py-3 flex justify-between items-center">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-stone mb-1">Ship to</p>
                          <p className="text-sm text-ink">{form.address_line1}{form.address_line2 ? `, ${form.address_line2}` : ''}</p>
                          {(selectedCity || selectedArea) && (
                            <p className="text-sm text-ink-soft">{[selectedArea?.name, selectedCity].filter(Boolean).join(', ')}</p>
                          )}
                        </div>
                        <button type="button" onClick={() => setStep(1)} className="text-xs text-stone underline hover:text-ink shrink-0">Edit</button>
                      </div>
                      {(selectedArea || !hasShippingZones) && (
                        <div className="px-4 py-3 flex justify-between items-center">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-stone mb-1">Delivery</p>
                            <p className="text-sm text-ink">
                              {shippingFee === 0 ? 'Free shipping' : format(shippingFee ?? 0)}
                              {selectedArea
                                ? effectiveDeliveryTime(selectedArea) ? ` · ${effectiveDeliveryTime(selectedArea)}` : ''
                                : settings?.shipping?.default_delivery_time
                                ? ` · Est. ${settings.shipping.default_delivery_time}`
                                : ''}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {checkoutError && (
                  <div className="flex items-start gap-2 rounded-xl border border-clay/30 bg-clay/5 px-4 py-3 text-sm text-clay">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="mt-0.5 shrink-0">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    {checkoutError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={placeOrder.isPending || !hasCartItems}
                  className="w-full rounded-full bg-clay py-4 text-[13px] font-semibold uppercase tracking-widest text-cream shadow-lg transition-all hover:bg-clay-light hover:shadow-xl disabled:opacity-50 active:scale-[0.98]"
                >
                  {placeOrder.isPending
                    ? <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                        </svg>
                        Placing your order…
                      </span>
                    : `Place order · ${format(grandTotal)}`
                  }
                </button>

                {/* Security strip */}
                <div className="flex items-center justify-center gap-3 text-[11px] text-stone pt-1">
                  <span className="flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    256-bit SSL
                  </span>
                  <span className="text-stone-light">·</span>
                  <span>Secure payment</span>
                  <span className="text-stone-light">·</span>
                  <span>Easy returns</span>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Right column — sticky summary */}
        <div className="lg:sticky lg:top-24">
          <OrderSummary
            shippingFee={shippingFee}
            estimatedDays={selectedArea ? effectiveDeliveryTime(selectedArea) : null}
            customerEmail={form.customer_email || undefined}
          />
        </div>
      </div>
    </Container>
  );
}
